import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabase';
import { localiseError } from './errors.js';

const CL = {
  bark: '#3D2B1F', gold: '#C9A96E', goldL: '#E8D5B0',
  cream: '#FFFDF8', sand: '#F5EFE6', stone: '#8B7D6B', terra: '#C0562B',
};

const FONT = {
  body: "'Figtree', 'Helvetica Neue', sans-serif",
  display: "'Cormorant Garamond', Georgia, serif",
};

const inp = {
  width: '100%', padding: '12px 14px', borderRadius: 8,
  border: '1px solid', fontSize: 15, outline: 'none',
  boxSizing: 'border-box', fontFamily: FONT.body, background: CL.sand,
  color: CL.bark,
};

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Attach listener first, then check getSession — PKCE flow (supabase-js v2)
    // exchanges the code during page load before useEffect runs, so the SIGNED_IN
    // event fires before the listener is attached. getSession catches that case.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        setReady(true);
      }
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setReady(true);
    });
    return () => subscription.unsubscribe();
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr('');
    if (password !== confirm) { setErr('Passwords do not match.'); return; }
    if (password.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setErr(localiseError(error, 'en'));
    } else {
      setMsg('Password set! Taking you in…');
      setTimeout(() => navigate('/'), 1500);
    }
    setLoading(false);
  }

  return (
    <div style={{
      minHeight: '100svh', background: CL.sand,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{
        width: '100%', maxWidth: 360,
        background: CL.cream, border: `1px solid ${CL.goldL}`, borderRadius: 14,
        padding: '36px 28px', boxShadow: '0 6px 28px rgba(61,43,31,0.08)',
      }}>

        {/* Wordmark */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 32 }}>
          <span style={{
            display: 'inline-block', width: 9, height: 9, flexShrink: 0,
            background: CL.gold, transform: 'rotate(45deg)', marginRight: 8,
          }} />
          <span style={{ fontFamily: FONT.body, fontSize: 13, fontWeight: 700, letterSpacing: '2.4px', color: CL.bark, textTransform: 'uppercase' }}>
            Tavelo
          </span>
        </div>

        {!ready ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: FONT.body, color: CL.stone, fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>
              Verifying link…
            </div>
            <div style={{ fontFamily: FONT.body, fontSize: 12, color: CL.stone }}>
              If nothing happens, the link may have expired.{' '}
              <span
                style={{ color: CL.bark, cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => navigate('/')}
              >
                Back to login
              </span>
            </div>
          </div>
        ) : msg ? (
          <div style={{ textAlign: 'center', fontFamily: FONT.body, color: CL.bark, fontSize: 15 }}>{msg}</div>
        ) : (
          <>
            <div style={{ fontFamily: FONT.display, fontStyle: 'italic', fontWeight: 500, fontSize: 28, color: CL.bark, textAlign: 'center', marginBottom: 6 }}>
              Choose a password.
            </div>
            <div style={{ fontFamily: FONT.body, fontSize: 13, color: CL.stone, textAlign: 'center', marginBottom: 24 }}>
              You'll use this every time you log in. At least 8 characters.
            </div>
            <form onSubmit={handleSubmit}>
              <input
                style={{ ...inp, marginBottom: 10, borderColor: err ? CL.terra : CL.goldL }}
                type="password" placeholder="New password" value={password}
                onChange={e => { setPassword(e.target.value); setErr(''); }}
                autoFocus required autoComplete="new-password"
              />
              <input
                style={{ ...inp, marginBottom: 12, borderColor: err ? CL.terra : CL.goldL }}
                type="password" placeholder="Confirm password" value={confirm}
                onChange={e => { setConfirm(e.target.value); setErr(''); }}
                required autoComplete="new-password"
              />
              {err && (
                <div style={{ textAlign: 'center', fontFamily: FONT.body, fontSize: 13, color: CL.terra, marginBottom: 10 }}>{err}</div>
              )}
              <button type="submit" disabled={loading} style={{
                width: '100%', padding: '13px 0', borderRadius: 8, border: 'none',
                fontFamily: FONT.body, fontWeight: 700, fontSize: 15, cursor: loading ? 'default' : 'pointer',
                background: CL.gold, color: CL.bark, opacity: loading ? 0.7 : 1, marginTop: 4,
              }}>
                {loading ? '…' : 'Set password →'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
